import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiShield, FiCheckCircle, FiChevronRight, FiChevronLeft, FiActivity, FiEye, FiFileText, FiBox } from 'react-icons/fi';
import { patientService, inventoryService } from '../../services/api';
import { toast } from 'react-toastify';

const NewPatient = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [inventoryFrames, setInventoryFrames] = useState([]);
  const [inventoryLenses, setInventoryLenses] = useState([]);
  const [inventoryAccessories, setInventoryAccessories] = useState([]);
  
  const [showManualFrame, setShowManualFrame] = useState(false);
  const [showManualLens, setShowManualLens] = useState(false);

  const [formData, setFormData] = useState({
    name: '', dob: '', age: '', contact: '', address: '',
    today_date: new Date().toISOString().split('T')[0],
    medical_history: '', family_history: '', allergies: '', first_wear: 'no',
    habitual_rx: {
      R: { sph: '', cyl: '', axis: '', dva: '', ph: '', add: '', nva: '' },
      L: { sph: '', cyl: '', axis: '', dva: '', ph: '', add: '', nva: '' }
    },
    visual_acuity: {
      R: { unaided: { num: '', den: '', sign: '+' }, ph: { num: '', den: '', sign: '+' }, aided: { num: '', den: '', sign: '+' } },
      L: { unaided: { num: '', den: '', sign: '+' }, ph: { num: '', den: '', sign: '+' }, aided: { num: '', den: '', sign: '+' } }
    },
    ocular_movement: '',
    convergence: '',
    color_vision: '',
    auto_refraction: {
      R: { sph: '', cyl: '', axis: '', dva: '', ph: '', add: '', nva: '' },
      L: { sph: '', cyl: '', axis: '', dva: '', ph: '', add: '', nva: '' }
    },
    retinoscopy: {
      R: { sph: '', cyl: '', axis: '', dva: '', ph: '', add: '', nva: '' },
      L: { sph: '', cyl: '', axis: '', dva: '', ph: '', add: '', nva: '' }
    },
    subjective: {
      R: { sph: '', cyl: '', axis: '', dva: '', ph: '', add: '', nva: '' },
      L: { sph: '', cyl: '', axis: '', dva: '', ph: '', add: '', nva: '' }
    },
    ophthalmoscopy: '',
    slit_lamp: '',
    dispensing_details: {
      pd: '',
      segment_height: '',
      frame_brand: '',
      frame_material: 'plastic',
      frame_metal_type: 'full_frame',
      frame_demographic: 'unisex',
      reading_glasses: 'none',
      lens_type: 'none',
      cr39_sv_type: 'minus',
      cr39_bifocal_type: 'normal',
      cr39_bifocal_shape: 'round_top',
      accessory_id: '',
      accessory_name: '',
      remarks: ''
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'dob') calculateAge(value);
  };

  React.useEffect(() => {
    const fetchInventory = async () => {
      try {
        const inv = await inventoryService.getAll();
        setInventoryFrames(inv.filter(i => i.category === 'frame'));
        setInventoryLenses(inv.filter(i => i.category === 'lens'));
        setInventoryAccessories(inv.filter(i => i.category === 'accessory'));
      } catch (e) {
        console.error('Failed to load inventory', e);
      }
    };
    fetchInventory();
  }, []);

  const handleRxChange = (eye, field, value) => {
    setFormData(prev => ({
      ...prev,
      habitual_rx: {
        ...prev.habitual_rx,
        [eye]: { ...prev.habitual_rx[eye], [field]: value }
      }
    }));
  };

  const handleVaChange = (eye, type, field, value) => {
    setFormData(prev => ({
      ...prev,
      visual_acuity: {
        ...prev.visual_acuity,
        [eye]: {
          ...prev.visual_acuity[eye],
          [type]: { ...prev.visual_acuity[eye][type], [field]: value }
        }
      }
    }));
  };

  const handleRefractionChange = (table, eye, field, value) => {
    setFormData(prev => ({
      ...prev,
      [table]: {
        ...prev[table],
        [eye]: { ...prev[table][eye], [field]: value }
      }
    }));
  };

  const handleDispensingChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-fill logic for frame selection
    if (name === 'frame_brand') {
      const selectedFrame = inventoryFrames.find(f => f.item_name === value);
      if (selectedFrame && selectedFrame.details) {
        const details = typeof selectedFrame.details === 'string' ? JSON.parse(selectedFrame.details) : selectedFrame.details;
        setFormData(prev => ({
          ...prev,
          dispensing_details: { 
            ...prev.dispensing_details, 
            [name]: value,
            frame_material: details.material || 'plastic',
            frame_demographic: details.demographic || 'unisex',
            frame_metal_type: details.metal_frame_type || 'full_frame'
          }
        }));
        return;
      }
    }

    // Capture accessory name if ID is selected
    if (name === 'accessory_id') {
      const acc = inventoryAccessories.find(a => a.id.toString() === value);
      setFormData(prev => ({
        ...prev,
        dispensing_details: { 
          ...prev.dispensing_details, 
          [name]: value, 
          accessory_name: acc ? acc.item_name : '' 
        }
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      dispensing_details: { ...prev.dispensing_details, [name]: value }
    }));
  };

  const toggleManualFrame = () => {
    setShowManualFrame(!showManualFrame);
    setFormData(prev => ({
      ...prev,
      dispensing_details: { ...prev.dispensing_details, frame_brand: '' }
    }));
  };

  const toggleManualLens = () => {
    setShowManualLens(!showManualLens);
    setFormData(prev => ({
      ...prev,
      dispensing_details: { ...prev.dispensing_details, lens_type: 'none' }
    }));
  };

  const calculateAge = (dobString) => {
    if (!dobString) return;
    const birthDate = new Date(dobString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    setFormData(prev => ({ ...prev, age: age >= 0 ? age : '' }));
  };

  const nextStep = () => {
    // Basic validation for step 1
    if (step === 1 && (!formData.name || !formData.dob || !formData.contact || !formData.address)) {
      toast.error('Please complete all identification fields.');
      return;
    }
    setStep(prev => prev + 1);
  };
  const prevStep = () => setStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== 6) return;
    
    setLoading(true);
    try {
      const result = await patientService.add(formData);
      toast.success('Patient Registered Successfully');
      
      setTimeout(() => {
        navigate('/admin/payments', {
          state: {
            patient: {
              id: result.id,
              reg_no: result.reg_no,
              name: formData.name,
              contact: formData.contact,
              dispensing_details: formData.dispensing_details
            }
          }
        });
      }, 1200);
      
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0
    })
  };

  const stepsList = [
    { id: 1, name: 'Identification', icon: <FiUser /> },
    { id: 2, name: 'Clinical Records', icon: <FiFileText /> },
    { id: 3, name: 'Measurements', icon: <FiActivity /> },
    { id: 4, name: 'Refraction', icon: <FiEye /> },
    { id: 5, name: 'Dispensing', icon: <FiBox /> },
    { id: 6, name: 'Review', icon: <FiCheckCircle /> }
  ];

  return (
    <div className="flex h-screen bg-[#f8fafc] font-poppins text-slate-900 overflow-hidden">
      {/* Sidebar Progress Tracker */}
      <aside className="w-72 bg-white border-r border-slate-200 flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.02)] z-20 flex-shrink-0">
        <div className="p-8 pb-4">
          <div onClick={() => navigate('/admin/patients')} className="flex items-center space-x-3 group cursor-pointer text-slate-400 hover:text-syana-blue-600 transition-colors mb-8">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-syana-blue-50 transition-colors">
               <FiChevronLeft className="text-lg" />
            </div>
            <span className="font-bold text-xs uppercase tracking-widest mt-0.5">Exit Form</span>
          </div>
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tight text-slate-900 leading-none">Patient</h2>
            <h2 className="text-2xl font-black uppercase tracking-tight text-slate-900 leading-tight">Registration</h2>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-syana-blue-600 mt-2">Clinical Portal</p>
          </div>
        </div>

        <nav className="flex-1 px-6 py-4 space-y-1 overflow-y-auto no-scrollbar">
          {stepsList.map((item) => {
            const isActive = step === item.id;
            const isCompleted = step > item.id;
            return (
              <div
                key={item.id}
                className={`flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all duration-300 ${
                  isActive 
                    ? 'bg-slate-900 text-white shadow-xl shadow-slate-200 translate-x-1' 
                    : isCompleted ? 'bg-syana-blue-50 text-syana-blue-600' : 'text-slate-400 bg-transparent opacity-60'
                }`}
              >
                <span className={`text-lg transition-transform duration-300 ${isActive ? 'scale-110 text-syana-blue-400' : ''}`}>{item.icon}</span>
                <span className="font-bold text-xs uppercase tracking-widest">{item.name}</span>
              </div>
            );
          })}
        </nav>
        
        <div className="p-6 border-t border-slate-100 bg-slate-50/50">
           <div className="w-full flex items-center space-x-3 text-slate-400 opacity-50">
               <FiShield className="text-lg" />
               <span className="text-[10px] font-bold uppercase tracking-widest leading-tight">Secure Health<br/>Data Entry</span>
           </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden bg-[#f8fafc]">
        {/* Sticky Header inside Main Content */}
        <header className="h-20 flex-shrink-0 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-10 sticky top-0 z-10 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
          <div className="flex items-center space-x-4">
             <div className="w-10 h-10 rounded-xl bg-slate-900 text-syana-blue-400 flex items-center justify-center font-black">
                {step.toString().padStart(2, '0')}
             </div>
             <div>
               <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 leading-none mb-1">Active Step</h2>
               <h1 className="text-sm font-bold text-slate-900 uppercase tracking-widest">
                 {stepsList.find(s => s.id === step)?.name}
               </h1>
             </div>
          </div>
          <div className="flex items-center space-x-4">
             <div className="text-right">
                <p className="text-xs font-bold text-slate-900 leading-none">Syana Staff</p>
                <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">Clinical Intake</p>
             </div>
             <div className="w-10 h-10 bg-syana-blue-50 text-syana-blue-600 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
                <FiUser />
             </div>
          </div>
        </header>

        {/* Scrollable Form Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-12 no-scrollbar">
           <form onSubmit={handleSubmit} className="w-full max-w-[1400px] mx-auto flex flex-col bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-xl border border-slate-100 min-h-full overflow-hidden">
             <div className="p-8 md:p-12 flex-1 relative">
             <AnimatePresence mode="wait" custom={step === 1 ? -1 : 1}>
                {step === 1 && (
                  <motion.div
                    key="step1"
                    custom={-1}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="w-full"
                  >
                    <div className="space-y-8">

                      <div className="flex items-center space-x-3 text-slate-900 border-b border-slate-100 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-syana-blue-50 text-syana-blue-600 flex items-center justify-center">
                           <FiUser className="text-lg" />
                        </div>
                        <div>
                           <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Step 01</h3>
                           <h4 className="font-bold text-sm text-slate-900">Identification</h4>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-6">
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Full Legal Name</label>
                          <input required name="name" value={formData.name} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-semibold transition-all shadow-sm" placeholder="e.g. John Doe" />
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-2 group">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Date of Birth</label>
                            <input required type="date" name="dob" value={formData.dob} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-semibold transition-all shadow-sm" />
                          </div>
                          <div className="space-y-2 group">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Age (Auto)</label>
                            <input readOnly value={formData.age} className="w-full px-4 py-3 bg-slate-100/50 border border-slate-100 rounded-lg text-sm font-bold text-slate-400 cursor-not-allowed shadow-inner" placeholder="0" />
                          </div>
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Primary Contact</label>
                          <input required name="contact" value={formData.contact} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-semibold transition-all shadow-sm" placeholder="07XXXXXXXX" />
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Residence Address</label>
                          <textarea required name="address" value={formData.address} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-semibold h-24 resize-none transition-all shadow-sm" placeholder="Enter full address..." />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    custom={1}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="w-full"
                  >
                    <div className="space-y-8">
                      <div className="flex items-center space-x-3 text-slate-900 border-b border-slate-100 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center">
                           <FiShield className="text-lg" />
                        </div>
                        <div>
                           <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Step 02</h3>
                           <h4 className="font-bold text-sm text-slate-900">Clinical Records</h4>
                        </div>
                      </div>
                      
                      <div className="space-y-6">
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest group-focus-within:text-orange-500 transition-colors">Medical / Surgical History</label>
                          <textarea name="medical_history" value={formData.medical_history} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-orange-400 focus:ring-4 focus:ring-orange-50 text-sm font-semibold h-24 resize-none placeholder:font-medium placeholder:text-slate-300 transition-all shadow-sm" placeholder="Previous surgeries, conditions, or 'None'" />
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-orange-500 transition-colors">Family Ocular History</label>
                          <textarea name="family_history" value={formData.family_history} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-orange-400 focus:ring-4 focus:ring-orange-50 text-sm font-semibold h-24 resize-none placeholder:font-medium placeholder:text-slate-300 transition-all shadow-sm" placeholder="Family history of glaucoma, diabetes, etc." />
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-orange-500 transition-colors">Known Allergies</label>
                          <input name="allergies" value={formData.allergies} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-orange-400 focus:ring-4 focus:ring-orange-50 text-sm font-semibold transition-all shadow-sm placeholder:font-medium placeholder:text-slate-300" placeholder="e.g. Penicillin, Pollen, or 'None'" />
                        </div>
                        <div className="space-y-4 pt-4">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Internal Eye Health / First Time Wear?</label>
                          <div className="flex gap-4">
                            {['yes', 'no'].map((val) => (
                              <label key={val} className={`flex-1 flex items-center justify-center p-4 rounded-lg border-2 transition-all cursor-pointer ${formData.first_wear === val ? 'bg-slate-900 border-slate-900 text-white shadow-md translate-y-[-1px]' : 'bg-slate-50 border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-widest hover:bg-slate-100'}`}>
                                <input type="radio" name="first_wear" value={val} checked={formData.first_wear === val} onChange={handleInputChange} className="hidden" />
                                {val === 'yes' ? 'Yes, First Time' : 'No, Regular'}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    key="step3"
                    custom={1}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="w-full"
                  >
                    <div className="space-y-10">
                      <div className="flex items-center space-x-3 text-slate-900 border-b border-slate-100 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                           <FiActivity className="text-lg" />
                        </div>
                        <div>
                           <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Step 03</h3>
                           <h4 className="font-bold text-sm text-slate-900">Clinical Measurements</h4>
                        </div>
                      </div>
                      
                      {/* Visual Acuity Table */}
                      <div className="space-y-4">
                         <h5 className="font-bold text-xs uppercase tracking-widest text-slate-500 pl-2 border-l-2 border-purple-500">Visual Acuity (VA)</h5>
                         <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                           <div className="overflow-x-auto">
                             <table className="w-full text-center divide-y divide-slate-200 min-w-[600px]">
                               <thead className="bg-slate-100/50">
                                 <tr>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200 w-16">Eye</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">VA (Unaided)</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">VA (PH)</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">VA (A)</th>
                                 </tr>
                               </thead>
                               <tbody className="divide-y divide-slate-200 bg-white">
                                 {['R', 'L'].map((eye) => (
                                   <tr key={`va-${eye}`} className="hover:bg-slate-50/50 transition-colors group">
                                     <td className="px-4 py-3 border-r border-slate-200">
                                       <span className="font-black text-sm text-slate-900">{eye}</span>
                                     </td>
                                     {['unaided', 'ph', 'aided'].map((type) => (
                                       <td key={`va-${eye}-${type}`} className="p-2 border-r border-slate-200 last:border-r-0">
                                         <div className="flex items-center justify-center space-x-1">
                                            <input 
                                              type="text" 
                                              value={formData.visual_acuity[eye][type].num} 
                                              onChange={(e) => handleVaChange(eye, type, 'num', e.target.value)}
                                              className="w-10 h-8 px-1 text-center bg-transparent border-b-2 border-slate-200 focus:border-purple-500 focus:bg-purple-50/30 font-bold text-sm text-slate-900 transition-all outline-none" 
                                              placeholder="6" 
                                            />
                                            <span className="text-slate-400 font-black">/</span>
                                            <input 
                                              type="text" 
                                              value={formData.visual_acuity[eye][type].den} 
                                              onChange={(e) => handleVaChange(eye, type, 'den', e.target.value)}
                                              className="w-10 h-8 px-1 text-center bg-transparent border-b-2 border-slate-200 focus:border-purple-500 focus:bg-purple-50/30 font-bold text-sm text-slate-900 transition-all outline-none" 
                                              placeholder="9" 
                                            />
                                            <select 
                                              value={formData.visual_acuity[eye][type].sign}
                                              onChange={(e) => handleVaChange(eye, type, 'sign', e.target.value)}
                                              className="h-8 pr-6 pl-1 bg-transparent border-b-2 border-slate-200 focus:border-purple-500 text-sm font-bold text-slate-600 outline-none cursor-pointer"
                                            >
                                              <option value="+">+</option>
                                              <option value="-">-</option>
                                              <option value="none"> </option>
                                            </select>
                                         </div>
                                       </td>
                                     ))}
                                   </tr>
                                 ))}
                               </tbody>
                             </table>
                           </div>
                         </div>
                      </div>

                      {/* Habitual RX Table */}
                      <div className="space-y-4">
                         <h5 className="font-bold text-xs uppercase tracking-widest text-slate-500 pl-2 border-l-2 border-syana-blue-500">Habitual RX</h5>
                         <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                           <div className="overflow-x-auto">
                             <table className="w-full text-center divide-y divide-slate-200 min-w-[700px]">
                               <thead className="bg-slate-100/50">
                                 <tr>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200 w-16">Eye</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">SPH</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">CYL</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">AXIS</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">DVA</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">PH</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">ADD</th>
                                   <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">NVA</th>
                                 </tr>
                               </thead>
                               <tbody className="divide-y divide-slate-200 bg-white">
                                 {['R', 'L'].map((eye) => (
                                   <tr key={`rx-${eye}`} className="hover:bg-slate-50/50 transition-colors group">
                                     <td className="px-4 py-3 border-r border-slate-200 bg-slate-50/50">
                                       <span className="font-black text-sm text-slate-900">{eye}</span>
                                     </td>
                                     {['sph', 'cyl', 'axis', 'dva', 'ph', 'add', 'nva'].map((field) => (
                                       <td key={`${eye}-${field}`} className="p-0 border-r border-slate-200 last:border-r-0">
                                         <input
                                           type="text"
                                           value={formData.habitual_rx[eye][field]}
                                           onChange={(e) => handleRxChange(eye, field, e.target.value)}
                                           className="w-full h-12 px-4 bg-transparent border-none focus:ring-2 focus:ring-syana-blue-400 focus:bg-syana-blue-50/20 text-center font-bold text-sm text-slate-900 transition-all placeholder:text-slate-300 placeholder:font-medium outline-none"
                                           placeholder="-"
                                         />
                                       </td>
                                     ))}
                                   </tr>
                                 ))}
                               </tbody>
                             </table>
                           </div>
                         </div>
                      </div>

                      {/* Observations */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-purple-600 transition-colors">Ocular Movement / Tropia of Foria</label>
                          <input name="ocular_movement" value={formData.ocular_movement} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-purple-400 focus:ring-4 focus:ring-purple-50 text-sm font-semibold transition-all shadow-sm" placeholder="e.g. SAFE" />
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-purple-600 transition-colors">Convergence</label>
                          <input name="convergence" value={formData.convergence} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-purple-400 focus:ring-4 focus:ring-purple-50 text-sm font-semibold transition-all shadow-sm" placeholder="e.g. NPC 8cm" />
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-purple-600 transition-colors">Color Vision</label>
                          <input name="color_vision" value={formData.color_vision} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-purple-400 focus:ring-4 focus:ring-purple-50 text-sm font-semibold transition-all shadow-sm" placeholder="e.g. Normal" />
                        </div>
                      </div>
 
                    </div>
                  </motion.div>
                )}

                {step === 4 && (
                  <motion.div
                    key="step4"
                    custom={1}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="w-full"
                  >
                    <div className="space-y-10">
                      <div className="flex items-center space-x-3 text-slate-900 border-b border-slate-100 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-syana-blue-50 text-syana-blue-600 flex items-center justify-center">
                           <FiActivity className="text-lg" />
                        </div>
                        <div>
                           <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Step 04</h3>
                           <h4 className="font-bold text-sm text-slate-900">Refraction</h4>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        {[
                          { key: 'auto_refraction', title: 'Auto Refraction', color: 'border-syana-blue-500' },
                          { key: 'retinoscopy', title: 'Retinoscopy', color: 'border-purple-500' },
                          { key: 'subjective', title: 'Subjective', color: 'border-green-500' }
                        ].map((section) => (
                          <div key={section.key} className="space-y-4">
                             <h5 className={`font-bold text-xs uppercase tracking-widest text-slate-500 pl-2 border-l-2 ${section.color}`}>{section.title}</h5>
                             <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                               <div className="overflow-x-auto no-scrollbar">
                                 <table className="w-full text-center divide-y divide-slate-200 min-w-[500px]">
                                   <thead className="bg-slate-100/50">
                                     <tr>
                                       <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200 w-16">Eye</th>
                                       <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">SPH</th>
                                       <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">CYL</th>
                                       <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">AXIS</th>
                                       <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">DVA</th>
                                       <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">PH</th>
                                       <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-r border-slate-200">ADD</th>
                                       <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">NVA</th>
                                     </tr>
                                   </thead>
                                   <tbody className="divide-y divide-slate-200 bg-white">
                                     {['R', 'L'].map((eye) => (
                                       <tr key={`${section.key}-${eye}`} className="hover:bg-slate-50/50 transition-colors group">
                                         <td className="px-4 py-3 border-r border-slate-200 bg-slate-50/50">
                                           <span className="font-black text-sm text-slate-900">{eye}</span>
                                         </td>
                                         {['sph', 'cyl', 'axis', 'dva', 'ph', 'add', 'nva'].map((field) => (
                                           <td key={`${section.key}-${eye}-${field}`} className="p-0 border-r border-slate-200 last:border-r-0">
                                             <input
                                               type="text"
                                               value={formData[section.key][eye][field]}
                                               onChange={(e) => handleRefractionChange(section.key, eye, field, e.target.value)}
                                               className="w-full h-12 px-2 bg-transparent border-none focus:ring-2 focus:ring-syana-blue-400 focus:bg-syana-blue-50/20 text-center font-bold text-sm text-slate-900 transition-all placeholder:text-slate-300 placeholder:font-medium outline-none"
                                               placeholder="-"
                                             />
                                           </td>
                                         ))}
                                       </tr>
                                     ))}
                                   </tbody>
                                 </table>
                               </div>
                             </div>
                          </div>
                        ))}
                      </div>

                      {/* Diagnostic Observations */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Ophthalmoscopy</label>
                          <textarea name="ophthalmoscopy" value={formData.ophthalmoscopy} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-semibold h-24 resize-none transition-all shadow-sm placeholder:text-slate-300 placeholder:font-medium" placeholder="Ophthalmoscopy observations..." />
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Slit Lamp Microscopy</label>
                          <textarea name="slit_lamp" value={formData.slit_lamp} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-semibold h-24 resize-none transition-all shadow-sm placeholder:text-slate-300 placeholder:font-medium" placeholder="Slit lamp microscopy observations..." />
                        </div>
                      </div>

              
                    </div>
                  </motion.div>
                )}

                {step === 5 && (
                  <motion.div
                    key="step5"
                    custom={1}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="w-full"
                  >
                    <div className="space-y-10">
                      <div className="flex items-center space-x-3 text-slate-900 border-b border-slate-100 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
                           <FiUser className="text-lg" />
                        </div>
                        <div>
                           <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Step 05</h3>
                           <h4 className="font-bold text-sm text-slate-900">Dispensing & Inventory</h4>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Core Dispensing Measurements */}
                        <div className="space-y-6">
                           <h5 className="font-bold text-xs uppercase tracking-widest text-slate-500 pl-2 border-l-2 border-orange-500">Measurements</h5>
                           <div className="grid grid-cols-2 gap-6 bg-slate-50 p-6 rounded-xl border border-slate-200">
                             <div className="space-y-2 group">
                               <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-orange-600 transition-colors">Pupillary Distance (PD)</label>
                               <div className="relative">
                                 <input name="pd" value={formData.dispensing_details.pd} onChange={handleDispensingChange} className="w-full pl-4 pr-10 py-3 bg-white border border-slate-200 rounded-lg focus:border-orange-400 focus:ring-4 focus:ring-orange-50 text-sm font-semibold transition-all shadow-sm" placeholder="e.g. 62" />
                                 <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">mm</span>
                               </div>
                             </div>
                             <div className="space-y-2 group">
                               <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-orange-600 transition-colors">Segment Height</label>
                               <div className="relative">
                                 <input name="segment_height" value={formData.dispensing_details.segment_height} onChange={handleDispensingChange} className="w-full pl-4 pr-10 py-3 bg-white border border-slate-200 rounded-lg focus:border-orange-400 focus:ring-4 focus:ring-orange-50 text-sm font-semibold transition-all shadow-sm" placeholder="e.g. 18" />
                                 <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">mm</span>
                               </div>
                             </div>
                           </div>

                            <h5 className="font-bold text-xs uppercase tracking-widest text-slate-500 pl-2 border-l-2 border-syana-blue-500">Frame Selection</h5>
                            <div className="space-y-4 bg-slate-50 p-6 rounded-xl border border-slate-200">
                              <div className="space-y-2 group">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Frame Selection</label>
                                  <button 
                                    type="button"
                                    onClick={toggleManualFrame}
                                    className={`text-[9px] font-black px-2 py-1 rounded border transition-all ${showManualFrame ? 'bg-syana-blue-600 text-white border-syana-blue-600' : 'bg-white text-slate-400 border-slate-200 hover:border-syana-blue-400'}`}
                                  >
                                    {showManualFrame ? '✓ MANUAL MODE' : '+ MANUAL ENTRY'}
                                  </button>
                                </div>
                                
                                {showManualFrame ? (
                                  <input 
                                    name="frame_brand" 
                                    value={formData.dispensing_details.frame_brand} 
                                    onChange={handleDispensingChange} 
                                    className="w-full px-4 py-3 bg-white border-2 border-syana-blue-400 rounded-lg text-sm font-bold shadow-sm focus:ring-4 focus:ring-syana-blue-50" 
                                    placeholder="Enter Frame Name Manually" 
                                  />
                                ) : (
                                  <select name="frame_brand" value={formData.dispensing_details.frame_brand} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 shadow-sm">
                                    <option value="">Select frame from stock...</option>
                                    <option value="patient_own">Patient's Own Frame</option>
                                    {inventoryFrames.map(f => (
                                      <option key={f.id} value={f.item_name}>{f.item_name} (Stock: {f.quantity})</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                             <div className="grid grid-cols-2 gap-6">
                               <div className="space-y-2 group">
                                 <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Demographic</label>
                                 <select name="frame_demographic" value={formData.dispensing_details.frame_demographic} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 shadow-sm">
                                   <option value="unisex">Unisex</option>
                                   <option value="gents">Gents</option>
                                   <option value="women">Women</option>
                                   <option value="children">Children</option>
                                 </select>
                               </div>
                               <div className="space-y-2 group">
                                 <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Material</label>
                                 <select name="frame_material" value={formData.dispensing_details.frame_material} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 shadow-sm">
                                   <option value="plastic">Plastic</option>
                                   <option value="metal">Metal</option>
                                   {/* <option value="rimless">Rimless</option> */}
                                   
                                 </select>
                               </div>
                             </div>
                             {formData.dispensing_details.frame_material === 'metal' && (
                               <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-2 group">
                                 <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-syana-blue-600">Metal Frame Type</label>
                                 <select name="frame_metal_type" value={formData.dispensing_details.frame_metal_type} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-syana-blue-50 border border-syana-blue-200 rounded-lg text-sm font-bold text-syana-blue-900 cursor-pointer outline-none focus:ring-4 focus:ring-syana-blue-200 shadow-inner">
                                   <option value="full_frame">Full Frame</option>
                                   <option value="half_rimless">Half Rimless</option>
                                   <option value="full_rimless">Full Rimless</option>
                                 </select>
                               </motion.div>
                             )}
                           </div>
                        </div>

                        {/* Miscellaneous Inventory Details */}
                        <div className="space-y-6">
                           <h5 className="font-bold text-xs uppercase tracking-widest text-slate-500 pl-2 border-l-2 border-purple-500">Reading Glasses (If Applicable)</h5>
                           <div className="space-y-4 bg-slate-50 p-6 rounded-xl border border-slate-200 h-fit">
                             <div className="space-y-4">
                                <label className="flex items-center space-x-3 p-4 rounded-lg border-2 border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                                  <input type="radio" name="reading_glasses" value="none" checked={formData.dispensing_details.reading_glasses === 'none'} onChange={handleDispensingChange} className="w-4 h-4 text-purple-600 focus:ring-purple-500" />
                                  <span className="text-sm font-bold text-slate-700">None</span>
                                </label>
                                <label className="flex items-center space-x-3 p-4 rounded-lg border-2 border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                                  <input type="radio" name="reading_glasses" value="plastic" checked={formData.dispensing_details.reading_glasses === 'plastic'} onChange={handleDispensingChange} className="w-4 h-4 text-purple-600 focus:ring-purple-500" />
                                  <span className="text-sm font-bold text-slate-700">Reading Version: Plastic</span>
                                </label>
                                <label className="flex items-center space-x-3 p-4 rounded-lg border-2 border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                                  <input type="radio" name="reading_glasses" value="metal" checked={formData.dispensing_details.reading_glasses === 'metal'} onChange={handleDispensingChange} className="w-4 h-4 text-purple-600 focus:ring-purple-500" />
                                  <span className="text-sm font-bold text-slate-700">Reading Version: Metal</span>
                                </label>
                                <label className="flex items-center space-x-3 p-4 rounded-lg border-2 border-syana-blue-200 bg-syana-blue-50/50 cursor-pointer hover:bg-syana-blue-50 transition-colors">
                                  <input type="radio" name="reading_glasses" value="bluecut" checked={formData.dispensing_details.reading_glasses === 'bluecut'} onChange={handleDispensingChange} className="w-4 h-4 text-syana-blue-600 focus:ring-syana-blue-500" />
                                  <span className="text-sm font-bold text-syana-blue-800">Reading with Bluecut Lenses</span>
                                </label>
                             </div>
                           </div>
                        </div>

                        {/* Lens Details */}
                         <div className="space-y-6 md:col-span-2">
                            <h5 className="font-bold text-xs uppercase tracking-widest text-slate-500 pl-2 border-l-2 border-teal-500">Lens Details</h5>
                            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                              <div className="space-y-4">
                                 <div className="space-y-2 group">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-teal-600">Primary Lens Type</label>
                                    <button 
                                      type="button"
                                      onClick={toggleManualLens}
                                      className={`text-[9px] font-black px-2 py-1 rounded border transition-all ${showManualLens ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-400 border-slate-200 hover:border-teal-400'}`}
                                    >
                                      {showManualLens ? '✓ MANUAL MODE' : '+ MANUAL ENTRY'}
                                    </button>
                                  </div>

                                  {showManualLens ? (
                                    <input 
                                      name="lens_type" 
                                      value={formData.dispensing_details.lens_type} 
                                      onChange={handleDispensingChange} 
                                      className="w-full px-4 py-3 bg-white border-2 border-teal-400 rounded-lg text-sm font-bold shadow-sm focus:ring-4 focus:ring-teal-50" 
                                      placeholder="Enter Lens Details Manually" 
                                    />
                                  ) : (
                                    <select name="lens_type" value={formData.dispensing_details.lens_type} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:border-teal-400 focus:ring-4 focus:ring-teal-50 shadow-sm">
                                      <option value="none">Select Lens Type</option>
                                      {inventoryLenses.map(l => (
                                        <option key={l.id} value={l.item_name}>{l.item_name} - Rs. {parseFloat(l.price).toFixed(2)}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                               <AnimatePresence mode="wait">
                                 {formData.dispensing_details.lens_type.toLowerCase().replace(/\s+/g, '_') === 'cr39_single_vision' && (
                                   <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-2 group bg-teal-50/50 p-4 rounded-lg border border-teal-100">
                                     <label className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">Single Vision Version</label>
                                     <select name="cr39_sv_type" value={formData.dispensing_details.cr39_sv_type} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-white border border-teal-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:ring-4 focus:ring-teal-100 shadow-sm">
                                       <option value="minus">Minus</option>
                                       <option value="cylindrical">Cylindrical</option>
                                     </select>
                                   </motion.div>
                                 )}

                                 {formData.dispensing_details.lens_type.toLowerCase().replace(/\s+/g, '_') === 'cr39_bifocal' && (
                                   <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="grid grid-cols-2 gap-4 bg-teal-50/50 p-4 rounded-lg border border-teal-100">
                                     <div className="space-y-2 group">
                                       <label className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">Bifocal Type</label>
                                       <select name="cr39_bifocal_type" value={formData.dispensing_details.cr39_bifocal_type} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-white border border-teal-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:ring-4 focus:ring-teal-100 shadow-sm">
                                         <option value="normal">Normal</option>
                                         <option value="cylindrical">Cylindrical</option>
                                       </select>
                                     </div>
                                     <div className="space-y-2 group">
                                       <label className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">Bifocal Shape</label>
                                       <select name="cr39_bifocal_shape" value={formData.dispensing_details.cr39_bifocal_shape} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-white border border-teal-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:ring-4 focus:ring-teal-100 shadow-sm">
                                         <option value="round_top">Round Top</option>
                                         <option value="flat_top">Flat Top</option>
                                       </select>
                                     </div>
                                   </motion.div>
                                 )}
                               </AnimatePresence>
                             </div>
                           </div>
                        </div>

                        {/* Accessory Details */}
                        <div className="space-y-6 md:col-span-2">
                           <h5 className="font-bold text-xs uppercase tracking-widest text-slate-500 pl-2 border-l-2 border-orange-500">Accessory Addition</h5>
                           <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                             <div className="space-y-4">
                               <div className="space-y-2 group">
                                 <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-orange-600">Select Accessory</label>
                                 <select name="accessory_id" value={formData.dispensing_details.accessory_id} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-50 shadow-sm">
                                   <option value="">No Accessory Selected</option>
                                   {inventoryAccessories.map(a => (
                                     <option key={a.id} value={a.id}>{a.item_name} - Rs. {parseFloat(a.price).toFixed(2)} (Stock: {a.quantity})</option>
                                   ))}
                                 </select>
                               </div>
                             </div>
                           </div>
                        </div>

                        {/* Additional Notes */}
                        <div className="md:col-span-2 pt-4 border-t border-slate-200 mt-4">
                          <div className="space-y-2 group">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Remarks & Footnotes</label>
                            <textarea name="remarks" value={formData.dispensing_details.remarks} onChange={handleDispensingChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-semibold h-24 resize-none transition-all shadow-sm placeholder:text-slate-300 placeholder:font-medium" placeholder="Additional clinical remarks or crucial footnotes..." />
                          </div>
                        </div>
                      </div>

                   
                    </div>
                  </motion.div>
                )}

                {step === 6 && (
                  <motion.div
                    key="step6"
                    custom={1}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="w-full"
                  >
                    <div className="space-y-8">
                       <div className="flex items-center space-x-3 text-slate-900 border-b border-slate-100 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center">
                           <FiCheckCircle className="text-lg" />
                        </div>
                        <div>
                           <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Step 06</h3>
                           <h4 className="font-bold text-sm text-slate-900">Final Review</h4>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-sm">
                         {/* Personal Info */}
                         <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-4">
                           <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                             <h5 className="font-bold text-syana-blue-600 uppercase tracking-widest text-[10px]">Identification</h5>
                             <button type="button" onClick={() => setStep(1)} className="text-[10px] font-bold text-slate-400 hover:text-syana-blue-600 uppercase tracking-wider">Edit</button>
                           </div>
                           <div className="space-y-2">
                             <p><span className="text-slate-500 font-medium">Name:</span> <span className="font-bold text-slate-900">{formData.name || 'N/A'}</span></p>
                             <p><span className="text-slate-500 font-medium">DOB / Age:</span> <span className="font-bold text-slate-900">{formData.dob} ({formData.age} yrs)</span></p>
                             <p><span className="text-slate-500 font-medium">Contact:</span> <span className="font-bold text-slate-900">{formData.contact || 'N/A'}</span></p>
                             <p><span className="text-slate-500 font-medium">Address:</span> <span className="font-bold text-slate-900">{formData.address || 'N/A'}</span></p>
                           </div>
                         </div>

                         {/* Clinical */}
                         <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-4">
                           <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                             <h5 className="font-bold text-syana-blue-600 uppercase tracking-widest text-[10px]">Clinical Records</h5>
                             <button type="button" onClick={() => setStep(2)} className="text-[10px] font-bold text-slate-400 hover:text-syana-blue-600 uppercase tracking-wider">Edit</button>
                           </div>
                           <div className="space-y-2">
                             <p><span className="text-slate-500 font-medium">History:</span> <span className="font-bold text-slate-900">{formData.medical_history || 'None'}</span></p>
                             <p><span className="text-slate-500 font-medium">Allergies:</span> <span className="font-bold text-slate-900">{formData.allergies || 'None'}</span></p>
                             <p><span className="text-slate-500 font-medium">First Wear:</span> <span className="font-bold text-slate-900 capitalize">{formData.first_wear}</span></p>
                           </div>
                         </div>

                         {/* Dispensing */}
                         <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-4 lg:col-span-2">
                           <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                             <h5 className="font-bold text-syana-blue-600 uppercase tracking-widest text-[10px]">Dispensing Overview</h5>
                             <button type="button" onClick={() => setStep(5)} className="text-[10px] font-bold text-slate-400 hover:text-syana-blue-600 uppercase tracking-wider">Edit</button>
                           </div>
                           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                             <div>
                                <p className="text-slate-500 font-medium text-[10px] uppercase tracking-widest mb-1">Measurements</p>
                                <p className="font-bold text-slate-900">PD: {formData.dispensing_details.pd || '-'} mm</p>
                                <p className="font-bold text-slate-900">Seg Height: {formData.dispensing_details.segment_height || '-'} mm</p>
                             </div>
                             <div>
                                <p className="text-slate-500 font-medium text-[10px] uppercase tracking-widest mb-1">Frame Settings</p>
                                <p className="font-bold text-slate-900">{formData.dispensing_details.frame_brand || 'No Brand Selected'}</p>
                                <p className="font-bold text-slate-900 capitalize">{formData.dispensing_details.frame_demographic} | {formData.dispensing_details.frame_material}</p>
                             </div>
                             <div>
                                <p className="text-slate-500 font-medium text-[10px] uppercase tracking-widest mb-1">Lens Request</p>
                                <p className="font-bold text-slate-900 capitalize">{formData.dispensing_details.lens_type.replace(/_/g, ' ')}</p>
                                <p className="font-bold text-slate-900 capitalize">Reading: {formData.dispensing_details.reading_glasses}</p>
                             </div>
                           </div>
                         </div>
                      </div>
                    </div>
                  </motion.div>
                )}
             </AnimatePresence>
          </div>

          <div className="p-8 md:px-12 border-t border-slate-100 bg-slate-50 flex justify-between items-center z-20">
            <button type="button" onClick={() => navigate('/admin/patients')} className="px-6 py-3 text-slate-500 font-bold uppercase text-[10px] tracking-widest hover:text-red-500 transition-colors">
               Cancel
            </button>
            <div className="flex space-x-4">
              {step > 1 && (
                 <button type="button" onClick={prevStep} className="px-6 py-3.5 bg-white border border-slate-200 text-slate-600 rounded-lg font-bold uppercase text-[10px] tracking-widest shadow-sm hover:bg-slate-50 transition-all flex items-center space-x-2">
                    <FiChevronLeft className="text-sm" /><span>Back</span>
                 </button>
              )}
              {step < 5 ? (
                 <button type="button" onClick={nextStep} className="px-8 py-3.5 bg-slate-900 text-white rounded-lg font-bold uppercase text-[10px] tracking-widest shadow-md shadow-slate-200 hover:scale-[1.02] transition-all active:scale-95 flex items-center space-x-2 group">
                    <span>Continue</span><FiChevronRight className="text-sm group-hover:translate-x-1 transition-transform" />
                 </button>
              ) : step === 5 ? (
                 <button type="button" onClick={nextStep} className="px-8 py-3.5 bg-syana-blue-600 text-white rounded-lg font-bold uppercase text-[10px] tracking-widest shadow-md shadow-syana-blue-200 hover:scale-[1.02] transition-all active:scale-95 flex items-center space-x-2">
                    <span>Review and Submit</span><FiChevronRight className="text-sm" />
                 </button>
              ) : (
                 <button type="submit" disabled={loading} className="px-8 py-3.5 bg-green-600 text-white rounded-lg font-bold uppercase text-[10px] tracking-widest shadow-md shadow-green-200 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
                    {loading ? <span>Processing...</span> : <><FiCheckCircle className="text-sm" /><span>Confirm & Save Patient</span></>}
                 </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </main>
  </div>
  );
};

export default NewPatient;
